import React from 'react';
import type ConstituentSortFields from '@/constants/constituents/ConstituentSortFields';
import FilterOperator from '@/constants/forms/FilterOperator';
import type SortOrder from '@/constants/SortOrder';
import useAdvancedConstituentSearch from '@/lib/hooks/swr/useAdvancedConstituentSearch';
// eslint-disable-next-line import/no-cycle -- TODO: Fix this the next time the file is edited.
import {
  ConstituentSearchFormProvider,
  type ConstituentSearchFilterParams,
} from '@/modules/ConstituentSearch';
import { useQueryProvider } from '@/modules/QueryProvider';
import type AdvancedConstituentSearchParams from '@/types/AdvancedConstituentSearchParams';
import ConstituentDataList from './ConstituentDataList';
import ConstituentListViewHeader from './ConstituentListViewHeader';

function ConstituentListView() {
  const { query } = useQueryProvider<ConstituentSearchFilterParams>();

  const { customFieldColumns } = query;

  const searchParams = React.useMemo(() => {
    const {
      filters,
      advancedFilters,
      donationAgg,
      page,
      pageSize,
      sortBy,
      sortOrder,
      columns,
    } = query;
    const filtersParams = [...(filters || []), ...(advancedFilters || [])].map(
      (item) => {
        // The custom field checkbox is a special case. There is no `false` state
        // that we can search by. It's either checked or null. Instead we need to
        // do a search for the field using the `is_null`/`is_not_null` operators,
        // but we still want the UI to be consistent with other boolean fields.
        if (item.isCustomField && typeof item.value === 'boolean') {
          const { value, ...rest } = item;
          return {
            ...rest,
            operator: value
              ? FilterOperator.IS_NOT_NULL
              : FilterOperator.IS_NULL,
          };
        }
        return item;
      },
    );
    const result: AdvancedConstituentSearchParams = {
      filters: filtersParams,
      donationAgg: donationAgg ?? false,
      columns,
    };

    if (page && pageSize) {
      result.pageFrom = ((page as number) - 1) * (pageSize as number);
      result.pageSize = pageSize as number;
    }
    if (sortBy) {
      result.sortBy = sortBy as ConstituentSortFields;
    }
    if (sortOrder) {
      result.sortOrder = sortOrder as SortOrder;
    }

    return result;
  }, [query]);

  const { data, isLagging } = useAdvancedConstituentSearch(searchParams);

  const [selectedItems, setSelectedItems] = React.useState<string[]>([]);
  const [excludedItems, setExcludedItems] = React.useState<string[]>([]);

  return (
    <>
      <ConstituentSearchFormProvider>
        <ConstituentListViewHeader
          searchParams={searchParams}
          excludedItems={excludedItems}
          selectedItems={selectedItems}
          setExcludedItems={setExcludedItems}
          setSelectedItems={setSelectedItems}
        />
      </ConstituentSearchFormProvider>
      <ConstituentDataList
        columns={searchParams.columns}
        customFieldColumns={customFieldColumns}
        constituents={data?.constituents}
        excludedItems={excludedItems}
        isLagging={isLagging}
        selectedItems={selectedItems}
        setExcludedItems={setExcludedItems}
        setSelectedItems={setSelectedItems}
        totalRows={data?.totalHits}
      />
    </>
  );
}

export default ConstituentListView;
